// src/lib/managerDay.js
// STATUS: active | UPDATED: 2026-09-24
// LOG:
//   2026-09-24  New. The manager had four pages and no answer to "what do I do
//               right now".
//
// A MANAGER'S DAY, ASSEMBLED FROM HIS OWN SIX DISCIPLINES.
//
// The manager layer grew into `/manager/`, `/manager/steps/`,
// `/manager/morning/`, `/manager/one-on-one/` and `/team/`, which is five
// places and no front door. Somebody opening this between customers does not
// want a menu of tools, they want to know what is not done yet.
//
// So the day is his list, not ours. `managementSystem.dailyDisciplines` is six
// things and it has been sitting in the repo since August:
//
//   Morning Meeting, Salesperson One-on-Ones, Appointment Confirmation,
//   Manager Customer Contact, Unsold Customer Review, End-of-Day CRM Inspection
//
// THREE OF THE SIX HAPPEN SOMEWHERE ELSE, AND WE SAY SO.
//
// Appointment confirmation, manager customer contact and unsold review all live
// in the CRM. This product cannot see them and never will without an
// integration that would drag customer data into a database that must not hold
// it, which is the rule with legal teeth.
//
// Marking them plainly as "your CRM, not here" is the honest move and it is
// better than the two alternatives. Hiding them would quietly edit his method
// down to the parts our software happens to cover. Faking a tick would be the
// no-placeholder rule broken in the one place a manager is deciding what to do
// next.
//
// WHAT COUNTS AS DONE IS DELIBERATELY GENEROUS.
//
// A one on one with four of six people is a good morning, not a failure. This
// screen reports, it does not grade. The recognition line in his own scorecard
// is "Recognize process behaviors, not only deliveries", and a screen that only
// ever shows somebody what they have not finished is the opposite of that.

import { dayKey, addDays } from './localDate.js'

// Which of his six this product can actually observe, and by what.
export const DISCIPLINES = [
  {
    key: 'morning',
    name: 'Morning meeting',
    here: true,
    to: '/manager/morning/',
    blank: 'Not prepared yet',
  },
  {
    key: 'oneOnOnes',
    name: 'One on ones',
    here: true,
    to: '/manager/one-on-one/',
    blank: 'None yet today',
  },
  {
    key: 'appointments',
    name: 'Appointment confirmation',
    here: false,
    why: 'Your CRM and the appointment board. We cannot see it from here and should not try.',
  },
  {
    key: 'contact',
    name: 'Manager customer contact',
    here: false,
    why: 'Your CRM. This product holds no customer records on purpose.',
  },
  {
    key: 'unsold',
    name: 'Unsold customer review',
    here: false,
    why: 'Your CRM, before the opportunity goes cold. It is in the one on one form as a prompt.',
  },
  {
    key: 'audit',
    name: 'End of day inspection',
    here: true,
    to: '/manager/system/',
    blank: 'Not started',
  },
]

// SOMEBODY IS OVERDUE FOR A ONE ON ONE, and his rule is daily. Seven days is
// the honest alarm rather than two: a manager with a floor of eight is not
// getting to everybody every day, and a screen that says six people are overdue
// every single morning is a screen nobody reads twice.
export const ONE_ON_ONE_STALE_DAYS = 7

export function staleOneOnOnes(floor, oneOnOnes, today = dayKey()) {
  const last = {}
  for (const row of oneOnOnes || []) {
    const cur = last[row.salesperson_id]
    if (!cur || row.held_on > cur) last[row.salesperson_id] = row.held_on
  }
  const cutoff = addDays(today, -ONE_ON_ONE_STALE_DAYS)
  return (floor || [])
    .map((p) => ({ person: p, last: last[p.id] || null }))
    .filter((r) => !r.last || r.last < cutoff)
}

export function heldToday(oneOnOnes, today = dayKey()) {
  return (oneOnOnes || []).filter((r) => r.held_on === today)
}

// WAITING ON THE MANAGER, which is the only list on the screen that is somebody
// else being held up. It goes first for that reason.
export function waitingOnYou({ signoffs, dueInspections }) {
  const items = []
  for (const s of signoffs || []) {
    items.push({ kind: 'signoff', id: s.id, userId: s.user_id, stepId: s.step_id, at: s.requested_at || s.created_at })
  }
  for (const d of dueInspections || []) {
    items.push({ kind: 'inspection', id: d.id, userId: d.salesperson_id, on: d.inspect_on, correction: d.correction })
  }
  return items
}

// HIS WEEKLY RHYTHM, the two items of it this product can see.
//
// "One recorded or live role-play per salesperson" and "One product FFB
// presentation per salesperson". A drill in the coach is the first one. The
// second is not observable here and is listed as such rather than guessed at.
export function weeklyPractice(floor, drills) {
  const byPerson = {}
  for (const d of drills || []) byPerson[d.user_id] = (byPerson[d.user_id] || 0) + 1
  const done = (floor || []).filter((p) => (byPerson[p.id] || 0) > 0)
  return { done: done.length, of: (floor || []).length, byPerson }
}

// The whole point of the screen in one sentence, computed rather than written,
// so it is never wrong about its own state.
export function dayHeadline({ morning, heldCount, floorSize, waiting }) {
  if (waiting > 0) {
    return `${waiting} ${waiting === 1 ? 'person is' : 'people are'} waiting on you.`
  }
  if (!morning || morning === 'empty' || morning === 'none') {
    return 'The morning meeting is not prepared yet.'
  }
  if (floorSize > 0 && heldCount === 0) {
    return 'No one on ones yet today.'
  }
  if (floorSize > 0 && heldCount >= floorSize) {
    return 'Everybody has had a one on one today.'
  }
  if (heldCount > 0) {
    return `${heldCount} of ${floorSize} have had a one on one today.`
  }
  return 'Nothing is waiting on you.'
}
